/** Small, accessible control primitives shared by the parameter panel. */
import type { ComponentChildren, JSX } from 'preact';

interface FieldProps {
  label: string;
  value?: string;
  children: ComponentChildren;
}

export function Field({ label, value, children }: FieldProps) {
  return (
    <div class="field">
      <div class="field-label">
        <span>{label}</span>
        {value !== undefined && <span class="value">{value}</span>}
      </div>
      {children}
    </div>
  );
}

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  /** extra hint appended to the numeric readout, e.g. "off" at 0 */
  hint?: string;
  onChange: (v: number) => void;
}

export function Slider({ label, value, min, max, step = 1, hint, onChange }: SliderProps) {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <Field label={label} value={hint ? `${value} (${hint})` : String(value)}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        style={{ '--fill': `${pct}%` }}
        onInput={(e: JSX.TargetedEvent<HTMLInputElement>) =>
          onChange(Number(e.currentTarget.value))
        }
      />
    </Field>
  );
}

interface NumberFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  children?: ComponentChildren;
}

export function NumberField({ label, value, min, max, onChange, children }: NumberFieldProps) {
  return (
    <Field label={label}>
      <div class="row">
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          aria-label={label}
          onChange={(e: JSX.TargetedEvent<HTMLInputElement>) => {
            const v = Number(e.currentTarget.value);
            if (Number.isFinite(v)) onChange(Math.min(max, Math.max(min, Math.round(v))));
          }}
        />
        {children}
      </div>
    </Field>
  );
}

interface SelectProps<T extends string> {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
}

export function Select<T extends string>({ label, value, options, onChange }: SelectProps<T>) {
  return (
    <Field label={label}>
      <select
        value={value}
        aria-label={label}
        onChange={(e: JSX.TargetedEvent<HTMLSelectElement>) =>
          onChange(e.currentTarget.value as T)
        }
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

export function Toggle({ label, checked, onChange }: ToggleProps) {
  return (
    <label class="toggle">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(e: JSX.TargetedEvent<HTMLInputElement>) =>
          onChange(e.currentTarget.checked)
        }
      />
    </label>
  );
}

interface GroupProps {
  title: string;
  open?: boolean;
  children: ComponentChildren;
}

export function Group({ title, open = true, children }: GroupProps) {
  return (
    <details class="group" open={open}>
      <summary>{title}</summary>
      <div class="group-body">{children}</div>
    </details>
  );
}
